import { Button } from 'components/atoms';
import { Form } from 'components/organisms';
import {
  artistInitialValues,
  ArtistTabs,
} from 'components/organisms/forms/artist/ArtistForm';
import { FormikHelpers } from 'formik';
import { useMutateOptions } from 'hooks';
import { useParamId } from 'hooks/useParamId';
import middleware from 'middleware';
import { useTranslation } from 'react-i18next';
import { PiUserPlus, PiX } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetArtist, useUpdateArtist } from 'services/artists';
import {
  useChangeProfilePicture,
  useCreateProfilePicture,
  useDeleteProfilePicture,
} from 'services/artists/profilePicture';
import { IArtistMutationForm } from 'services/artists/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { getLastPathname } from 'utils/navbar/getParentPathname';

const EditArtist = () => {
  const artistId = useParamId('artist');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const backPathname = `/artist/${artistId}/${getLastPathname(pathname)}`;
  const { data: artist, isLoading } = useGetArtist(artistId);
  const updateArtist = useUpdateArtist();
  const changeProfilePicture = useChangeProfilePicture(artistId);
  const createProfilePicture = useCreateProfilePicture();
  const deleteProfilePicture = useDeleteProfilePicture(artistId);

  const mutateOptions = useMutateOptions<IArtistMutationForm>();

  const onUpdateArtist = async (
    values: IArtistMutationForm,
    helpers: FormikHelpers<IArtistMutationForm>
  ) => {
    const updatedArtist = middleware.artist.formToQuery(values);

    if (values.file) {
      artist?.profilePictureLink?.length
        ? await changeProfilePicture.mutateAsync(values.file)
        : await createProfilePicture.mutateAsync({
            id: artistId,
            file: values.file,
          });
    }

    if (
      artist?.profilePictureLink?.length !== values.profilePictureLink?.length
    ) {
      await deleteProfilePicture.mutateAsync();
    }

    await updateArtist.mutateAsync(
      { artistId: artistId, ...updatedArtist },
      {
        ...mutateOptions(helpers),
        onSuccess: () => navigate(backPathname),
      }
    );
  };

  return (
    <Form.Artist
      i18nextPrefix='artist'
      initialValues={
        artist ? middleware.artist.queryToForm(artist) : artistInitialValues
      }
      onSubmit={onUpdateArtist}
      customBackItem={
        <Button onlyIcon onClick={() => navigate(backPathname)}>
          <Button.Icon icon={<PiX />} />
        </Button>
      }
      topbarContent={
        <Button type='submit' variant={BUTTON_VARIANT.submit}>
          <Button.Text text={t('button.save')} />
          <Button.Icon icon={<PiUserPlus />} />
        </Button>
      }
      status={{ isLoading }}
      disableTabs={[ArtistTabs.bio, ArtistTabs.actions]}
    />
  );
};

export default EditArtist;
